import { useForm } from 'react-hook-form'
import { UserRequestInterface } from 'src/interfaces/user'
import { useAppDispatch, useAppSelector } from 'src/store'
import { register, updateCurrentFlow } from 'src/useCase/user'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { EMAIL_REGEX, KANA_REGEX, NO_EMOJI_REGEX } from 'src/utils/regex'
import LocalStorage from 'src/utils/LocalStorage'

export const useRegister = () => {
  const dispatch = useAppDispatch()
  const clinic = useAppSelector((state) => state.domain.clinic.detail.entity)

  const defaultValues: UserRequestInterface = {
    family_name: '',
    given_name: '',
    family_name_kana: '',
    given_name_kana: '',
    date_of_birth: '',
    gender_id: '',
    email: '',
    clinic_uuid: '',
    phone: '',
    custom_field_value: '',
  }

  // custom_field_value's required-ness is determined by the clinic's own
  // settings (custom_field_enabled + custom_field_required), fetched async
  // in UserInfoForm.tsx — rebuilt on every render so it reflects the latest
  // clinic data once it arrives.
  const isCustomFieldRequired = !!(
    clinic?.custom_field_enabled && clinic?.custom_field_required
  )

  const validationSchema = Yup.object().shape({
    family_name: Yup.string()
      .required('入力してください。')
      .max(50, '姓は30文字以下で入力してください'),
    given_name: Yup.string()
      .required('入力してください。')
      .max(100, '名は30文字以下で入力してください'),
    family_name_kana: Yup.string()
      .required('入力してください。')
      .max(50, '姓(カナ)は30文字以下で入力してください')
      .matches(KANA_REGEX, 'カナで入力してください。'),
    given_name_kana: Yup.string()
      .required('入力してください。')
      .max(100, '名(カナ)は30文字以下で入力してください')
      .matches(KANA_REGEX, 'カナで入力してください。'),
    date_of_birth: Yup.string().nullable(),
    email: Yup.string()
      .required('入力してください。') //必須項目が入力されていません
      .email('メールアドレスを正しく入力してください')
      .matches(EMAIL_REGEX, 'メールアドレスを正しく入力してください'),
    gender_id: Yup.mixed().nullable(),
    phone: Yup.string().nullable(),
    custom_field_value: (isCustomFieldRequired
      ? Yup.string().required('入力してください。')
      : Yup.string().nullable()
    )
      // Whitespace-only input must be treated as empty (same as leaving the
      // field blank), both for the required check above and before sending
      // the value to the API.
      .transform((value) => (typeof value === 'string' ? value.trim() : value))
      .max(255, '255文字以下で入力してください')
      .matches(NO_EMOJI_REGEX, {
        message: '絵文字は入力できません。',
        excludeEmptyString: true,
      }),
  })

  const onSubmit = async (
    data: UserRequestInterface,
    redirectPath: string,
    currentFlow: string | null,
  ): Promise<boolean> => {
    try {
      data.clinic_uuid = LocalStorage.getClinicUuid()
      const action: any = await dispatch(
        register({ credentials: data, meta: form, redirectPath }),
      )

      if (action?.meta?.requestStatus === 'rejected') return false

      if (currentFlow && LocalStorage.accessToken) {
        await dispatch(updateCurrentFlow({ current_flow: currentFlow }))
      }
      return true
    } catch {
      return false
    }
  }

  const form = useForm<UserRequestInterface>({
    defaultValues,
    shouldUnregister: false,
    shouldFocusError: true,
    mode: 'all',
    resolver: yupResolver(validationSchema),
  })

  return { form, onSubmit }
}
